
import { normalize } from './index';

// Number-line encoding contract — shared between validators and renderer.

/** Prefix that marks NL drag values. Uses \x00 (null byte) — cannot appear in
 *  user-typed text, making `startsWith(NL_DRAG_PREFIX)` an exact discriminant. */
export const NL_DRAG_PREFIX = '\x00nl-drag\x00' as const;

/** Returns true only for values produced by `encodeNLDragValue`. */
export const isNLDragEncoded = (v: string): boolean => v.startsWith(NL_DRAG_PREFIX);

/** Encodes a chip drop as `NL_DRAG_PREFIX + label + "@" + position`.
 *  Both chip identity and placement travel together for validation. */
export const encodeNLDragValue = (label: string, position: number): string =>
  `${NL_DRAG_PREFIX}${label}@${String(position)}`;


/**
 * Validates a drag answer by comparing chip label and snap position independently.
 *
 * - `expectedLabel`    — the correct answer value (from `answer=` on the widget)
 * - `expectedPosition` — the axis value where the chip must land (from `POSITION[value=…]` in the DSL)
 * @example
 * // POSITION[value=3] with NUMERIC_INPUT[answer=7]: chip '7' must land at 3
 * isNLDragCorrect(encodeNLDragValue('7', 3), '7', 3)   // → true
 */
export function isNLDragCorrect(
  encoded: string,
  expectedLabel: string | number,
  expectedPosition: string | number
): boolean {
  const payload  = encoded.slice(NL_DRAG_PREFIX.length);
  const atIdx    = payload.lastIndexOf('@'); // lastIndexOf: position is always the last segment, labels may contain '@'
  const label    = normalize(payload.slice(0, atIdx));
  const position = normalize(payload.slice(atIdx + 1));
  return label === normalize(expectedLabel) && position === normalize(expectedPosition);
}
